import { Controller } from "@hotwired/stimulus"

/**
 * Validation page (the tab the user shares with the wizard).
 *
 * Single responsibility: poll the server for the share's recording state and,
 * once the wizard tab confirms it's seeing frames, swap the UI to the active
 * state and arm the loyalty-login link.
 *
 * The wizard tab is the analyzer (it owns the MediaStream). This page must
 * never call getDisplayMedia itself — see ScreenShareSessionsController for
 * the architecture rationale.
 *
 * Why JS-driven navigation (instead of a plain anchor):
 * the loyalty-login URL is a side-effecting GET (it stamps a click + 303s to
 * the loyalty provider). Any browser feature that speculatively fetches links
 * on hover/preload would burn the click before the user actually intends it.
 * We therefore keep the URL in JS state and only navigate inside a click
 * handler.
 */
export default class extends Controller {
  static targets = ["waiting", "active", "stopped", "openButton", "challengeCode"]
  static values = {
    stateUrl: String,
    pollMs:   { type: Number, default: 1500 }
  }

  connect() {
    this.recordingActive = false
    this.recordingStarted = false
    this.loyaltyLoginUrl = null
    this.poll()
    this.timer = setInterval(() => this.poll(), this.pollMsValue)
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer)
  }

  async poll() {
    try {
      const res = await fetch(this.stateUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`state ${res.status}`)
      const data = await res.json()
      this.applyState(data)
    } catch {
      // Soft-fail: the wizard tab is the source of truth. Transient blips
      // shouldn't alarm the user, so we keep polling silently.
    }
  }

  applyState(data) {
    if (data.challenge_code) this.updateChallengeCode(data.challenge_code)

    if (data.recording_active) {
      this.showActive(data.loyalty_login_url)
    } else if (this.recordingStarted) {
      this.showStopped()
    } else {
      this.showWaiting()
    }
  }

  /** Refresh the small on-page code element when the server rotates it.
   *  We only swap textContent + the data attribute; styling is intentionally
   *  unchanged so a human watcher sees no movement around the code. */
  updateChallengeCode(code) {
    if (!this.hasChallengeCodeTarget) return
    if (this.challengeCodeTarget.textContent === code) return

    this.challengeCodeTarget.textContent = code
    this.challengeCodeTarget.dataset.validationChallengeCode = code
  }

  showWaiting() {
    this.recordingActive = false
    this.loyaltyLoginUrl = null
    this.waitingTarget.classList.remove("hidden")
    this.activeTarget.classList.add("hidden")
    if (this.hasStoppedTarget) this.stoppedTarget.classList.add("hidden")
    this.disableOpenButton()
  }

  showActive(loyaltyLoginUrl) {
    this.recordingStarted = true
    this.waitingTarget.classList.add("hidden")
    this.activeTarget.classList.remove("hidden")
    if (this.hasStoppedTarget) this.stoppedTarget.classList.add("hidden")

    if (loyaltyLoginUrl) {
      this.loyaltyLoginUrl = loyaltyLoginUrl
      this.enableOpenButton()
    }

    if (!this.recordingActive) {
      this.recordingActive = true
      this.flashTitle()
    }
  }

  showStopped() {
    this.recordingActive = false
    this.loyaltyLoginUrl = null
    this.waitingTarget.classList.add("hidden")
    this.activeTarget.classList.add("hidden")
    if (this.hasStoppedTarget) this.stoppedTarget.classList.remove("hidden")
    this.disableOpenButton()
  }

  enableOpenButton() {
    if (!this.hasOpenButtonTarget) return
    const btn = this.openButtonTarget
    btn.disabled = false
    btn.removeAttribute("aria-disabled")
    btn.classList.remove("opacity-60", "cursor-not-allowed")
  }

  disableOpenButton() {
    if (!this.hasOpenButtonTarget) return
    const btn = this.openButtonTarget
    btn.disabled = true
    btn.setAttribute("aria-disabled", "true")
    btn.classList.add("opacity-60", "cursor-not-allowed")
  }

  /** Same-tab navigation. Calling window.location.href inside the click
   *  handler keeps the recording attached to the same tab through the
   *  redirect, while bypassing any link-prefetch speculation. */
  openProgramme(event) {
    if (event) event.preventDefault()
    if (!this.loyaltyLoginUrl) return
    window.location.href = this.loyaltyLoginUrl
  }

  focusApplicationTab(event) {
    if (event) event.preventDefault()
    if (window.opener && !window.opener.closed) {
      window.opener.focus()
    } else {
      window.history.back()
    }
  }

  /** Flash the tab's title once so a user with this tab in the background
   *  notices the share went live and comes back to click "Open program". */
  flashTitle() {
    const original = document.title
    const messages = ["✓ Recording — open program", original]
    let i = 0
    const swap = () => { document.title = messages[i % messages.length]; i++ }
    swap()
    const flashTimer = setInterval(swap, 1200)
    setTimeout(() => { clearInterval(flashTimer); document.title = original }, 6000)
  }
}
